import React, {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { KibTextarea } from '@chewy/kib-fields-new-react'
import {
  KibAttachIcon,
  KibCloseIcon,
  KibMicIcon,
  KibMicOffIcon,
  KibSendIcon,
} from '@chewy/kib-icons-react'
import classNames from 'classnames'

import RhapHoverTooltip from '~/components/common/RhapHoverTooltip'
import RhapTextButton from '~/components/common/RhapTextButton'
import {
  SpeechRecognitionErrorType,
  useSpeechRecognition,
} from '~/hooks/useSpeechRecognition'
import {
  InputMethod,
  trackEvent,
  TrackingEvent,
} from '~/utils/trackingProvider'

import { useAgentChat } from '../AgentChatContext'
import ListeningIndicator from './ListeningIndicator'

import styles from './ChatInput.module.scss'

export interface ChatInputProps {
  disabled?: boolean
  isResponding?: boolean
  onSendMessage: (params: {
    content: string
    file?: { data: ArrayBuffer; name: string; type: string }
    inputMethod?: InputMethod
    promptMetadata?: Record<string, any>
    stateDelta?: Record<string, any>
  }) => void
  placeholder?: string
  sessionId: string | null
}

const ChatInput: React.FC<ChatInputProps> = ({
  isResponding = false,
  onSendMessage,
  disabled = false,
  placeholder = '',
  sessionId,
}) => {
  const MAX_TEXTAREA_HEIGHT = 160

  const { inputState, setInputState } = useAgentChat()
  const { selectedFile, messageCreatedViaVoice } = inputState

  // Ref for fast typing - bypasses React state updates on every keystroke
  const messageRef = useRef(inputState.message)

  const [baseMessage, setBaseMessage] = useState('') // Message before starting speech
  const [voiceSessionStartTime, setVoiceSessionStartTime] = useState<
    number | null
  >(null)

  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { t } = useTranslation('Agent')

  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`
    }
  }, [])

  const {
    audioStream,
    clearError,
    error: speechError,
    errorDetails,
    isListening,
    isSupported: isSpeechSupported,
    startListening,
    stopListening,
    transcript,
  } = useSpeechRecognition({
    autoStop: true,
    onTranscriptComplete: (finalTranscript) => {
      const combined = baseMessage
        ? `${baseMessage} ${finalTranscript}`
        : finalTranscript
      const trimmedMessage = combined.trim()
      messageRef.current = trimmedMessage
      if (textareaRef.current) {
        textareaRef.current.value = trimmedMessage
      }
      adjustTextareaHeight()
      setInputState((prev) => ({
        ...prev,
        message: trimmedMessage,
        messageCreatedViaVoice: true,
      }))
      setBaseMessage('')

      const duration = voiceSessionStartTime
        ? Date.now() - voiceSessionStartTime
        : null

      trackEvent(TrackingEvent.AI_ASSISTANT_SPEECH_TO_TEXT_COMPLETED, {
        had_existing_message: Boolean(baseMessage),
        transcript_length: finalTranscript.length,
        duration_ms: duration,
        agent_session_id: sessionId,
      })

      setVoiceSessionStartTime(null)
    },
  })

  // Update message field with interim transcript results
  useEffect(() => {
    if (isListening && transcript) {
      const combinedMessage = baseMessage
        ? `${baseMessage} ${transcript}`
        : transcript
      const trimmedMessage = combinedMessage.trim()
      messageRef.current = trimmedMessage
      if (textareaRef.current) {
        textareaRef.current.value = trimmedMessage
      }
      adjustTextareaHeight()
      setInputState((prev) => ({
        ...prev,
        message: trimmedMessage,
        messageCreatedViaVoice: false,
      }))
    }
  }, [
    transcript,
    isListening,
    baseMessage,
    setInputState,
    adjustTextareaHeight,
  ])

  // Update context state (no debouncing needed - React batches updates efficiently)
  const syncMessageToContext = useCallback(
    (value: string, preserveVoiceFlag = false) => {
      setInputState((prev) => ({
        ...prev,
        message: value,
        messageCreatedViaVoice: preserveVoiceFlag
          ? prev.messageCreatedViaVoice
          : false,
      }))
    },
    [setInputState],
  )

  const handleSend = async () => {
    // Sync before sending
    syncMessageToContext(messageRef.current)

    const message = messageRef.current
    if (message.trim() && !disabled && !isResponding) {
      const inputMethod = messageCreatedViaVoice
        ? InputMethod.SPEECH_TO_TEXT
        : InputMethod.TYPING

      let fileData:
        | { data: ArrayBuffer; name: string; type: string }
        | undefined

      if (selectedFile) {
        const arrayBuffer = await selectedFile.arrayBuffer()
        fileData = {
          data: arrayBuffer,
          name: selectedFile.name,
          type: selectedFile.type,
        }
      }

      onSendMessage({
        content: message.trim(),
        inputMethod,
        file: fileData,
      })

      messageRef.current = ''
      syncMessageToContext('')
      if (textareaRef.current) {
        textareaRef.current.value = ''
        textareaRef.current.style.height = 'auto'
      }
      setBaseMessage('')
      setVoiceSessionStartTime(null)
    }
  }

  const handleMessageChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    const newValue = e.target.value
    messageRef.current = newValue
    if (!isListening) {
      syncMessageToContext(newValue)
    }
    adjustTextareaHeight()
  }

  const handleBlur = () => {
    // Sync on blur to ensure state is up to date
    syncMessageToContext(messageRef.current)
  }

  // Sync from state to textarea only when state changes externally (e.g., clearInput, voice input)
  // Don't sync while user is actively typing to prevent overwriting their input
  useEffect(() => {
    const isTextareaFocused =
      textareaRef.current !== null &&
      document.activeElement === textareaRef.current

    // Only sync if state differs from ref AND user isn't actively typing
    if (inputState.message !== messageRef.current && !isTextareaFocused) {
      messageRef.current = inputState.message
      if (textareaRef.current) {
        textareaRef.current.value = inputState.message
        adjustTextareaHeight()
      }
    }
  }, [inputState.message, adjustTextareaHeight])

  const handleKeyPress = (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const handleMicClick = () => {
    if (isListening) {
      stopListening()
    } else {
      if (speechError) {
        clearError()
      }
      // Sync current message before starting voice input
      // Preserve messageCreatedViaVoice flag if it's already true (user may cancel without speaking)
      syncMessageToContext(messageRef.current, true)
      setBaseMessage(messageRef.current)
      startListening()
      setVoiceSessionStartTime(Date.now())
    }
  }

  const handleDragEnter = (e: React.DragEvent) => {
    // Only handle file drag events, not other drag operations
    const hasFiles =
      e.dataTransfer.types && e.dataTransfer.types.includes('Files')
    if (!hasFiles) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    const hasFiles =
      e.dataTransfer.types && e.dataTransfer.types.includes('Files')
    if (!hasFiles) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    const hasFiles =
      e.dataTransfer.types && e.dataTransfer.types.includes('Files')
    if (!hasFiles) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    const hasFiles =
      e.dataTransfer.types && e.dataTransfer.types.includes('Files')
    if (!hasFiles) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragging(false)

    const { files } = e.dataTransfer
    if (files && files.length > 0) {
      const [firstFile] = files
      setInputState((prev) => ({
        ...prev,
        selectedFile: firstFile,
      }))
    }
  }

  const handleRemoveFile = () => {
    setInputState((prev) => ({
      ...prev,
      selectedFile: null,
    }))
  }

  // Determine if mic should be disabled based on error type
  const isMicDisabled =
    disabled ||
    errorDetails?.type === SpeechRecognitionErrorType.MICROPHONE_DENIED

  const getMicButtonTitle = () => {
    if (!errorDetails) {
      return isListening
        ? t('Agent:STOP_LISTENING')
        : t('Agent:START_LISTENING')
    }

    switch (errorDetails.type) {
      case SpeechRecognitionErrorType.MICROPHONE_DENIED:
        return t('Agent:MICROPHONE_ACCESS_DENIED')
      case SpeechRecognitionErrorType.NETWORK_ERROR:
        return t('Agent:SPEECH_NETWORK_ERROR')
      case SpeechRecognitionErrorType.NO_SPEECH:
        return t('Agent:SPEECH_NO_SPEECH_DETECTED')
      case SpeechRecognitionErrorType.START_FAILED:
        return t('Agent:SPEECH_START_FAILED')
      default:
        return t('Agent:SPEECH_ERROR')
    }
  }

  const micButtonTitle = getMicButtonTitle()

  const renderMicButtonTrigger = useCallback(
    ({
      ref,
      onMouseEnter,
      onMouseLeave,
      'aria-describedby': ariaDescribedBy,
    }: {
      'aria-describedby': string
      onMouseEnter: () => void
      onMouseLeave: () => void
      ref: React.Ref<HTMLElement>
    }) => (
      <span
        ref={ref}
        style={{ display: 'inline-block' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <RhapTextButton
          iconOnly
          aria-describedby={ariaDescribedBy}
          aria-label={micButtonTitle}
          disabled={isMicDisabled}
          emphasis="primary"
          icon={isListening ? <KibMicOffIcon /> : <KibMicIcon />}
          size="medium"
          title={micButtonTitle}
          onClick={handleMicClick}
        />
      </span>
    ),
    [isMicDisabled, isListening, micButtonTitle, handleMicClick],
  )

  return (
    <div
      className={styles.inputContainer}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className={styles.dropZoneOverlay}>
          <div className={styles.dropZoneContent}>
            <KibAttachIcon height={24} width={24} />
            <span>{t('Agent:DROP_FILE_HERE')}</span>
          </div>
        </div>
      )}
      {selectedFile && (
        <div className={styles.filePreview}>
          <div className={styles.fileInfo}>
            <KibAttachIcon />
            <span className={styles.fileName}>{selectedFile.name}</span>
            <span className={styles.fileSize}>
              ({Math.max(1, Math.round(selectedFile.size / 1024))} KB)
            </span>
          </div>
          <button
            aria-label={t('Agent:REMOVE_FILE')}
            className={styles.removeFileButton}
            type="button"
            onClick={handleRemoveFile}
          >
            <KibCloseIcon />
          </button>
        </div>
      )}
      <div className={styles.inputWrapper}>
        <div className={styles.messageInput}>
          <KibTextarea
            className={styles.textarea}
            defaultValue={inputState.message}
            disabled={disabled || isListening}
            label=""
            placeholder={placeholder}
            renderControl={(attrs) => (
              <textarea
                {...attrs}
                className={classNames(styles.textareaInput, attrs.className)}
                ref={textareaRef}
              />
            )}
            rows={1}
            onBlur={handleBlur}
            onChange={handleMessageChange}
            onKeyPress={handleKeyPress}
          />
        </div>
        <div className={styles.buttonContainer}>
          <div
            className={classNames(styles.listeningIndicatorWrapper, {
              [styles.hidden]: !isListening,
            })}
          >
            <ListeningIndicator audioStream={audioStream} />
          </div>
          {isSpeechSupported && (
            <RhapHoverTooltip
              content={micButtonTitle}
              enabled={Boolean(errorDetails)}
              placement="top"
              trigger={renderMicButtonTrigger}
            />
          )}
          <div
            className={classNames(styles.sendButtonWrapper, {
              [styles.hidden]: isListening,
            })}
          >
            <RhapTextButton
              iconOnly
              aria-label={t('Agent:SEND_MESSAGE')}
              disabled={disabled || !messageRef.current.trim() || isResponding}
              emphasis="primary"
              icon={<KibSendIcon />}
              size="medium"
              onClick={handleSend}
            />
          </div>
        </div>
      </div>
      <div className={styles.disclaimer}>
        {t('Agent:AI_RESPONSE_DISCLAIMER')}
      </div>
    </div>
  )
}

export default ChatInput
